import React, { useMemo } from "react";
import type { AsyncJob } from "../types/api";
import { getAsyncJobDisplayMessage, isNorthflankLogAccessWarning, isNorthflankRateLimitWarning } from "../lib/asyncJobs";

const STAGE_LABELS: Record<string, string> = {
  download: "Download ADE",
  "historical-import": "Import storico SSH",
  "legacy-sync": "Sync vecchio PC",
  "xml-processing": "XML processing",
  bronzing: "Bronzing",
  dbt: "DBT",
  golding: "Golding",
};

const STAGE_ORDER: Record<string, number> = {
  download: 10,
  "historical-import": 20,
  "legacy-sync": 25,
  "xml-processing": 30,
  bronzing: 40,
  dbt: 50,
  golding: 60,
};

const STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  running: "Running",
  success: "OK",
  failed: "KO",
};

const STATUS_CLASSES: Record<string, string> = {
  queued: "bg-slate-500/15 text-slate-300 border-slate-400/20",
  running: "bg-sky-500/15 text-sky-300 border-sky-400/20",
  success: "bg-emerald-500/15 text-emerald-300 border-emerald-400/20",
  failed: "bg-rose-500/15 text-rose-300 border-rose-400/20",
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "n/d";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
};

const shortRun = (value?: string) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.length <= 12 ? text : `${text.slice(0, 10)}…`;
};

interface AsyncJobQueueProps {
  jobs: AsyncJob[];
  loading?: boolean;
  selectedJobId?: string | null;
  onSelectJob: (job: AsyncJob) => void;
}

interface TenantGroup {
  tenantKey: string;
  jobs: AsyncJob[];
}

export function AsyncJobQueue({ jobs, loading = false, selectedJobId, onSelectJob }: AsyncJobQueueProps) {
  const visibleJobs = useMemo(
    () => jobs.filter((job) => job.source === "northflank"),
    [jobs],
  );

  const tenantGroups = useMemo<TenantGroup[]>(() => {
    const grouped = new Map<string, Map<string, AsyncJob>>();

    for (const job of visibleJobs) {
      const keys = (job.tenant_codes && job.tenant_codes.length)
        ? job.tenant_codes
        : [job.tenant || job.company || "n/d"];
      for (const rawKey of keys) {
        const tenantKey = String(rawKey || "").trim() || "n/d";
        if (!grouped.has(tenantKey)) grouped.set(tenantKey, new Map<string, AsyncJob>());
        grouped.get(tenantKey)!.set(job.id, job);
      }
    }

    return [...grouped.entries()]
      .map(([tenantKey, tenantJobs]) => ({
        tenantKey,
        jobs: [...tenantJobs.values()].sort((a, b) => {
          const aActive = a.status === "queued" || a.status === "running";
          const bActive = b.status === "queued" || b.status === "running";
          if (aActive !== bActive) return aActive ? -1 : 1;
          const stageDelta = (STAGE_ORDER[a.stage] || 999) - (STAGE_ORDER[b.stage] || 999);
          if (stageDelta !== 0) return stageDelta;
          return String(b.updated_at || "").localeCompare(String(a.updated_at || ""));
        }),
      }))
      .sort((a, b) => {
        const aActive = a.jobs.some((job) => job.status === "queued" || job.status === "running");
        const bActive = b.jobs.some((job) => job.status === "queued" || job.status === "running");
        if (aActive !== bActive) return aActive ? -1 : 1;
        return a.tenantKey.localeCompare(b.tenantKey);
      });
  }, [visibleJobs]);

  return (
    <div className="glass rounded-xl border border-white/10 p-4 flex flex-col min-h-[260px] max-h-[360px] overflow-hidden">
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-white/10">
        <div>
          <div className="text-sm font-semibold text-slate-100">Coda job async</div>
          <div className="text-xs text-slate-400 mt-1">
            XML, bronze, DBT, gold e sync Northflank raggruppati per tenant.
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
          {loading ? "sync..." : `${visibleJobs.length} job`}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pt-3 space-y-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
        {!tenantGroups.length && (
          <div className="rounded-xl border border-dashed border-white/10 px-4 py-5 text-sm text-slate-400">
            Nessun job async recente.
          </div>
        )}

        {tenantGroups.map((group) => {
          const activeCount = group.jobs.filter((job) => job.status === "queued" || job.status === "running").length;
          return (
            <div key={group.tenantKey} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-100 truncate">{group.tenantKey}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {activeCount ? `${activeCount} attivi` : "nessun attivo"}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 shrink-0">{group.jobs.length} step</div>
              </div>

              <div className="divide-y divide-white/5">
                {group.jobs.map((job) => (
                  <button
                    type="button"
                    key={`${group.tenantKey}-${job.id}`}
                    onClick={() => onSelectJob(job)}
                    className={`w-full text-left px-4 py-3 transition hover:bg-white/[0.06] ${
                      selectedJobId === job.id ? "bg-white/[0.08]" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-slate-100">
                            {STAGE_LABELS[job.stage] || job.stage}
                          </span>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${STATUS_CLASSES[job.status] || STATUS_CLASSES.queued}`}>
                            {STATUS_LABELS[job.status] || job.status}
                          </span>
                          {isNorthflankRateLimitWarning(job) && (
                            <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-200">
                              429 cached
                            </span>
                          )}
                          {isNorthflankLogAccessWarning(job) && !isNorthflankRateLimitWarning(job) && (
                            <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-200">
                              Log warning
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                            {job.source === "northflank" ? "Northflank" : "Locale"}
                          </span>
                          {(job.tenant_codes || []).length > 1 && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-amber-300">
                              multi-tenant
                            </span>
                          )}
                        </div>
                        <div className={`text-xs mt-1 break-words ${isNorthflankLogAccessWarning(job) || isNorthflankRateLimitWarning(job) ? "text-amber-200" : "text-slate-400"}`}>
                          {getAsyncJobDisplayMessage(job)}
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <div className="text-[11px] text-slate-300">
                          {job.run_id ? `run ${shortRun(job.run_id)}` : STAGE_LABELS[job.stage] || job.stage}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {formatDateTime(job.updated_at || job.started_at)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
